<script lang="ts">
import { DisplayMode, useDisplayConfigStore } from '@/stores/displayConfig'
import { defineComponent } from 'vue'

export enum ViewMode {
  AZ = 'A-Z',
  Popular = 'Popularity',
  Category = 'Category'
}

export interface MenuItem {
  title: string
  url: string
  category: string
  views: number
}

export default defineComponent({
  name: 'MegaMenu',
  data() {
    return {
      menuItems: [] as MenuItem[],
      categories: [] as string[],
      ViewMode,
      DisplayMode,
      viewMode: ViewMode.AZ,
      displayConfig: useDisplayConfigStore()
    }
  },
  async mounted() {
    const response = await fetch('/api/content/megamenu')
    if (response.ok) {
      this.menuItems = await response.json()

      this.menuItems = this.menuItems.filter((item) => item.category != 'Uncategorized')

      // compute categories this way to sort by view counts
      this.categories = Array.from(new Set(this.menuItems.map((item) => item.category)))
      var categorySums = this.categories
        .map((category) => {
          return {
            category: category,
            sum: this.menuItems
              .filter((item) => item.category == category)
              .reduce((acc, item) => acc + item.views, 0)
          }
        })
        .sort((a, b) => (b.sum > a.sum ? 1 : -1))
      this.categories = categorySums.map((categorySum) => categorySum.category)

      this.resortMenuItems()
    }
  },
  methods: {
    resortMenuItems() {
      if (this.viewMode == ViewMode.AZ) {
        this.menuItems.sort((a, b) => a.title.localeCompare(b.title))
      } else if (this.viewMode == ViewMode.Popular) {
        this.menuItems.sort((a, b) => (b.views > a.views ? 1 : -1))
      } else if (this.viewMode == ViewMode.Category) {
        this.menuItems.sort((a, b) => a.category.localeCompare(b.category))
      }
    },
    goTo(url: string) {
      this.$router.push(url)
      if (this.displayConfig.displayMode == DisplayMode.Mobile) {
        this.displayConfig.showingMegaMenu = false
      }
    }
  },
  watch: {
    viewMode() {
      this.resortMenuItems()
    }
  }
})
</script>

<template>
  <div id="megaMenuBody">
    <div id="megaMenuHeader">
      <template v-if="displayConfig.displayMode == DisplayMode.Mobile">
        <div class="d-flex flex-column" style="width: 100%">
          <div class="d-flex flex-row align-center justify-center" style="height: 25px">
            <v-btn
              @click="viewMode = ViewMode.Popular"
              variant="text"
              size="large"
              :ripple="false"
              class="me-2 text-none"
              :class="{
                'font-weight-black': viewMode == ViewMode.Popular,
                'text-decoration-underline': viewMode == ViewMode.Popular
              }"
              >By Popularity</v-btn
            >
            <div class="dot"></div>
            <v-btn
              @click="viewMode = ViewMode.AZ"
              variant="text"
              size="large"
              :ripple="false"
              class="me-2 text-none"
              :class="{
                'font-weight-black': viewMode == ViewMode.AZ,
                'text-decoration-underline': viewMode == ViewMode.AZ
              }"
              >A-Z</v-btn
            >
          </div>
          <div class="d-flex flex-row align-center justify-center" style="height: 25px">
            <v-btn
              @click="viewMode = ViewMode.Category"
              variant="text"
              size="large"
              :ripple="false"
              class="me-2 text-none"
              :class="{
                'font-weight-black': viewMode == ViewMode.Category,
                'text-decoration-underline': viewMode == ViewMode.Category
              }"
              >By Category</v-btn
            >
          </div>
        </div>
      </template>
      <template v-else>
        <v-btn
          @click="viewMode = ViewMode.Popular"
          variant="text"
          size="large"
          :ripple="false"
          class="me-2 text-none"
          :class="{
            'font-weight-black': viewMode == ViewMode.Popular,
            'text-decoration-underline': viewMode == ViewMode.Popular
          }"
          >By Popularity</v-btn
        >
        <div class="dot"></div>
        <v-btn
          @click="viewMode = ViewMode.AZ"
          variant="text"
          size="large"
          :ripple="false"
          class="me-2 text-none"
          :class="{
            'font-weight-black': viewMode == ViewMode.AZ,
            'text-decoration-underline': viewMode == ViewMode.AZ
          }"
          >A-Z</v-btn
        >
        <div class="dot"></div>
        <v-btn
          @click="viewMode = ViewMode.Category"
          variant="text"
          size="large"
          :ripple="false"
          class="me-2 text-none"
          :class="{
            'font-weight-black': viewMode == ViewMode.Category,
            'text-decoration-underline': viewMode == ViewMode.Category
          }"
          >By Category</v-btn
        >
      </template>
    </div>
    <div id="megaMenuItems" class="pb-6 pt-4 pl-4">
      <template v-if="viewMode != ViewMode.Category">
        <template v-for="item in menuItems" :key="item.title">
          <a @click="goTo(item.url)" class="font-weight-regular clickable">{{ item.title }}</a>
        </template>
      </template>
      <template v-else>
        <template v-for="category in categories" :key="category">
          <div class="font-weight-black">{{ category }}</div>
          <template
            v-for="item in menuItems.filter((mi) => mi.category == category)"
            :key="item.title"
          >
            <template v-if="item.category == category">
              <a @click="goTo(item.url)" class="ml-4 font-weight-regular clickable">{{
                item.title
              }}</a>
            </template>
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
#megaMenuBody {
  display: flex;
  flex-direction: column;
  max-width: 600px;
  width: calc(100vw - 40px);
  background-color: #424242;
  border: 5px groove var(--color-cu-gold);
  padding: 15px 30px;
  overflow-y: auto;
}

#megaMenuHeader {
  width: 100%;
  color: var(--color-cu-gold);
  text-align: center;
  line-height: 50px;
  font-size: 1.5em;
  display: flex;
  justify-content: center;
  align-items: center;
}

#megaMenuItems {
  color: #fff;
  display: flex;
  flex-direction: column;
  width: fit-content;
  align-self: center;
  max-height: 85vh;
}

#megaMenuItems a {
  color: #fff;
  font-size: 1em;
}

.dot {
  height: 10px;
  width: 10px;
  background-color: var(--color-cu-gold);
  border-radius: 50%;
  display: flex;
}

.clickable {
  cursor: pointer;
}
</style>
